/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import * as actions from './actions';
import {
    BladeContext,
    BladeOptions,
    InitialBlade,
    OpenBlade,
    ResizerProps,
} from './types';
import { setRelativeActive } from './utils';

export function useArrowKeyNavigation(
    onKeyLeft: () => void,
    onKeyRight: () => void,
): void {
    useEffect(() => {
        const setNextActiveBlade = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.altKey) {
                if (event.key === 'ArrowLeft') {
                    onKeyLeft();
                } else if (event.key === 'ArrowRight') {
                    onKeyRight();
                }
            }
        };
        document.addEventListener('keyup', setNextActiveBlade);
        return () => {
            document.removeEventListener('keyup', setNextActiveBlade);
        };
    }, [onKeyLeft, onKeyRight]);
}

export function useBlades(open: Array<InitialBlade> = []): BladeContext {
    const [blades, dispatch] = useState<OpenBlade[]>([]);
    const bladesRef = useRef<Array<OpenBlade>>(null);

    useEffect(() => {
        bladesRef.current = blades;
    });

    const closeBlades = useCallback((name: string) => {
        actions.closeSucceeding(dispatch, name);
    }, []);

    const closeBlade = useCallback((blade: OpenBlade) => {
        actions.closeBlade(dispatch, blade);
    }, []);

    const renderBlade = useCallback(
        <T,>(name: string, options: BladeOptions<T>): OpenBlade<T> => {
            const blade = createBlade<T>(name, options);
            actions.replaceOrAddBlade(dispatch, blade);
            return blade;
        },
        [],
    );

    const setActive = useCallback(name => {
        actions.activateBlade(dispatch, name);
    }, []);

    useArrowKeyNavigation(
        () => setRelativeActive(blades, setActive, -1),
        () => setRelativeActive(blades, setActive, 1),
    );

    useEffect(() => {
        open.forEach(o => renderBlade(o.name, o.options));
    }, [renderBlade, open]);

    return {
        actions: {
            showBlade: renderBlade,
            renderBlade,
            closeBlades,
            closeBlade,
        },
        blades,
    };

    function createBlade<T>(
        name: string,
        options: BladeOptions<T>,
    ): OpenBlade<T> {
        return {
            name,
            options,
            overrides: {},
            onClose() {
                actions.closeSucceeding(dispatch, name);
            },
            onActivate() {
                actions.activateBlade(dispatch, name);
            },
            onExpand() {
                actions.expandBlade(dispatch, name);
                actions.activateBlade(dispatch, name);
            },
            onCollapse() {
                actions.collapseBlade(dispatch, name);
                actions.activateBlade(dispatch, name);
            },
            onRestore() {
                actions.restoreBlade(dispatch, name);
                actions.activateBlade(dispatch, name);
            },
            onResize() {
                actions.updateBlade(dispatch, name, { fluid: false });
            },
            onResetSize() {
                actions.updateBlade(dispatch, name, { fluid: true });
            },
        };
    }
}

export function useResizer(
    minWidth: number,
    ref: RefObject<HTMLDivElement>,
): ResizerProps & { width: number } {
    const [width, setWidth] = useState<number>(minWidth);
    const [resizing, setResizing] = useState<boolean>(false);

    useEffect(() => {
        setWidth(minWidth);
    }, [minWidth]);

    const onResize = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const offsetWidth = ref.current?.offsetWidth;
        setWidth(offsetWidth);

        const containerLeft = event.clientX - offsetWidth;

        const startResize = (resizeEvent: MouseEvent) => {
            resizeEvent.preventDefault();
            resizeEvent.stopPropagation();

            let newWidth = resizeEvent.clientX - containerLeft;
            if (newWidth < minWidth) {
                newWidth = minWidth;
            }

            setResizing(true);
            setWidth(newWidth);
        };

        const endResize = (resizeEvent: MouseEvent) => {
            resizeEvent.preventDefault();
            resizeEvent.stopPropagation();
            window.removeEventListener('mousemove', startResize, false);
            window.removeEventListener('mouseup', endResize);
            window.addEventListener('mouseleave', endResize);
            setResizing(false);
        };

        window.addEventListener('mousemove', startResize, false);
        window.addEventListener('mouseup', endResize);
        window.addEventListener('mouseleave', endResize);
    };

    const onResetSize = () => {
        setResizing(false);
    };

    return { onResize, resizing, width, onResetSize };
}
